import { useState } from "react";
import { formatTimestamp } from "@/lib/date-utils";
import { useNavigate, useSearchParams, useLocation, useMatch } from "react-router-dom";
import { trpc } from "@/trpc";
import { UserStatus, UserType } from "@satyrsmc/shared/lib/enums";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search } from "lucide-react";
import { STATUS_LABELS, STATUS_COLORS, TYPE_LABELS } from "@/components/users/userDisplay";
import { RegistrationDetailSheet } from "@/components/users/RegistrationDetailSheet";
import { usePendingRegistrationCount } from "@/queries/users";
import { useUrlSearchSync } from "@/queries/hooks";
import type { Registration, RejectedRegistration } from "@satyrsmc/shared/dto/admin/user";

const PAGE_SIZE = 25;

type Tab = "users" | "pending" | "rejected";

type RegStatus = "all" | "ready" | "link-only";

export function UsersContactPanel() {
  const navigate = useNavigate();
  const onPending = !!useMatch("/admin/contacts/users/pending");
  const onRejected = !!useMatch("/admin/contacts/users/rejected");
  const activeTab: Tab = onPending ? "pending" : onRejected ? "rejected" : "users";

  function setTab(tab: Tab) {
    navigate(
      tab === "pending"
        ? "/admin/contacts/users/pending"
        : tab === "rejected"
          ? "/admin/contacts/users/rejected"
          : "/admin/contacts/users",
    );
  }

  const { count: registrationCount } = usePendingRegistrationCount();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Users</h1>
        <p className="mt-1 text-muted-foreground">
          Manage user accounts, registrations, and invitations.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList variant="line">
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="pending">
            Pending
            {registrationCount > 0 && (
              <span className="ml-1.5 inline-flex size-5 items-center justify-center rounded-full bg-yellow-400 text-xs font-semibold text-yellow-900">
                {registrationCount > 9 ? "9+" : registrationCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <UsersTab />
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          <PendingTab />
        </TabsContent>

        <TabsContent value="rejected" className="mt-4">
          <RejectedTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UsersTab() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStatus = searchParams.get("status") ?? "all";
  const rawType = searchParams.get("type") ?? "all";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const { search, setSearch } = useUrlSearchSync(searchParams, setSearchParams);
  const rawQ = searchParams.get("q") ?? "";

  const { data, isLoading } = trpc.admin.users.list.useQuery({
    status: rawStatus !== "all" ? (rawStatus as UserStatus) : undefined,
    user_type: rawType !== "all" ? (rawType as UserType) : undefined,
    q: rawQ || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function setPage(p: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p === 1) {
          next.delete("page");
        } else {
          next.set("page", String(p));
        }
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {isLoading ? "Loading…" : `${total} user${total === 1 ? "" : "s"} total`}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
        <Select
          value={rawStatus}
          onValueChange={(v) =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (v === "all") {
                  next.delete("status");
                } else {
                  next.set("status", v);
                }
                next.delete("page");
                return next;
              },
              { replace: true },
            )
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.values(UserStatus).map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={rawType}
          onValueChange={(v) =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (v === "all") {
                  next.delete("type");
                } else {
                  next.set("type", v);
                }
                next.delete("page");
                return next;
              },
              { replace: true },
            )
          }
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {Object.values(UserType).map((t) => (
              <SelectItem key={t} value={t}>
                {TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-4 py-3">Name</TableHead>
              <TableHead className="px-4 py-3">Username</TableHead>
              <TableHead className="px-4 py-3">Type</TableHead>
              <TableHead className="px-4 py-3">Status</TableHead>
              <TableHead className="px-4 py-3">Last Login</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              (["s0", "s1", "s2", "s3", "s4"] as const).map((k) => (
                <TableRow key={k}>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  </TableCell>
                </TableRow>
              ))
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow
                  key={u.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(`/admin/contacts/${u.contact_id}/user`, {
                      state: { from: location.pathname },
                    })
                  }
                >
                  <TableCell className="px-4 py-3 font-medium">
                    {u.display_name || u.email || "—"}
                  </TableCell>
                  <TableCell className="px-4 py-3 font-mono text-muted-foreground">
                    {u.username}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {TYPE_LABELS[u.user_type]}
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[u.user_status]}`}
                    >
                      {STATUS_LABELS[u.user_status]}
                    </span>
                  </TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {u.last_login ? formatTimestamp(u.last_login) : "Never"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="rounded border px-3 py-1 disabled:opacity-40 hover:bg-muted transition-colors"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              className="rounded border px-3 py-1 disabled:opacity-40 hover:bg-muted transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PendingTab() {
  const utils = trpc.useUtils();
  const { data: registrations, isLoading } = trpc.admin.users.listRegistrations.useQuery();
  const [selected, setSelected] = useState<Registration | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const rawRegStatus = (searchParams.get("status") ?? "ready") as RegStatus;
  const { search, setSearch } = useUrlSearchSync(searchParams, setSearchParams, {
    resetPage: false,
  });
  const rawQ = searchParams.get("q") ?? "";

  const filtered = (registrations ?? []).filter((r) => {
    if (rawRegStatus === "ready" && !r.is_ready_for_review) return false;
    if (rawRegStatus === "link-only" && r.is_ready_for_review) return false;
    if (!rawQ) return true;
    const q = rawQ.toLowerCase();
    return (
      r.email.toLowerCase().includes(q) ||
      (r.first_name ?? "").toLowerCase().includes(q) ||
      (r.last_name ?? "").toLowerCase().includes(q)
    );
  });

  const handleDone = () => {
    void utils.admin.users.listRegistrations.invalidate();
    void utils.admin.users.listRejectedRegistrations.invalidate();
    void utils.admin.users.pendingCount.invalidate();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search registrations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
        <Select
          value={rawRegStatus}
          onValueChange={(v) =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("status", v);
                return next;
              },
              { replace: true },
            )
          }
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Registration Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Show All Entries</SelectItem>
            <SelectItem value="ready">Ready for Review</SelectItem>
            <SelectItem value="link-only">Requested Link Only</SelectItem>
          </SelectContent>
        </Select>
        {!isLoading && (
          <p className="text-sm text-muted-foreground">
            {filtered.length} pending registration{filtered.length === 1 ? "" : "s"}
          </p>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-4 py-3">Email</TableHead>
              <TableHead className="px-4 py-3">Name</TableHead>
              <TableHead className="px-4 py-3">Status</TableHead>
              <TableHead className="px-4 py-3">Submitted</TableHead>
              <TableHead className="px-4 py-3">Flags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              (["s0", "s1", "s2", "s3", "s4"] as const).map((k) => (
                <TableRow key={k}>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-28 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-28 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  </TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  {rawRegStatus === "ready" && !rawQ
                    ? "No registrations ready for review."
                    : rawRegStatus === "all" && !rawQ
                      ? "No pending registrations."
                      : "No registrations match your filters."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((reg) => (
                <TableRow
                  key={reg.id}
                  className="cursor-pointer"
                  onClick={() => {
                    setSelected(reg);
                    setSheetOpen(true);
                  }}
                >
                  <TableCell className="px-4 py-3 font-medium">{reg.email}</TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {[reg.first_name, reg.last_name].filter(Boolean).join(" ") || "—"}
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    {reg.is_ready_for_review ? (
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        Ready for Review
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        Requested Link Only
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {reg.created_at ? formatTimestamp(reg.created_at) : "—"}
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <span className="flex flex-wrap gap-1">
                      {reg.contact_id && (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                          Contact
                        </span>
                      )}
                      {reg.member_id && (
                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                          Member
                        </span>
                      )}
                      {reg.invited_by && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          Invited
                        </span>
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <RegistrationDetailSheet
        registration={selected}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onDone={handleDone}
      />
    </div>
  );
}

function RejectedTab() {
  const utils = trpc.useUtils();
  const { data: rejections, isLoading } = trpc.admin.users.listRejectedRegistrations.useQuery();
  const [selected, setSelected] = useState<RejectedRegistration | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { search, setSearch } = useUrlSearchSync(searchParams, setSearchParams, {
    resetPage: false,
  });
  const rawQ = searchParams.get("q") ?? "";

  const filtered = (rejections ?? []).filter((r) => {
    if (!rawQ) return true;
    const q = rawQ.toLowerCase();
    return (
      r.email.toLowerCase().includes(q) ||
      (r.first_name ?? "").toLowerCase().includes(q) ||
      (r.last_name ?? "").toLowerCase().includes(q)
    );
  });

  const handleDone = () => {
    void utils.admin.users.listRegistrations.invalidate();
    void utils.admin.users.listRejectedRegistrations.invalidate();
    void utils.admin.users.pendingCount.invalidate();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search rejections…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
        {!isLoading && (
          <p className="text-sm text-muted-foreground">
            {filtered.length} rejected registration{filtered.length === 1 ? "" : "s"}
          </p>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-4 py-3">Email</TableHead>
              <TableHead className="px-4 py-3">Name</TableHead>
              <TableHead className="px-4 py-3">Rejected</TableHead>
              <TableHead className="px-4 py-3">Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              (["s0", "s1", "s2", "s3", "s4"] as const).map((k) => (
                <TableRow key={k}>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-28 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                  {rawQ ? "No rejections match your search." : "No rejected registrations."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((reg) => (
                <TableRow
                  key={reg.id}
                  className="cursor-pointer"
                  onClick={() => {
                    setSelected(reg);
                    setSheetOpen(true);
                  }}
                >
                  <TableCell className="px-4 py-3 font-medium">{reg.email}</TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {[reg.first_name, reg.last_name].filter(Boolean).join(" ") || "—"}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {formatTimestamp(reg.rejected_at)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-muted-foreground">
                    {reg.rejected_reason ? (
                      <span className="italic">"{reg.rejected_reason}"</span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <RegistrationDetailSheet
        registration={selected}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onDone={handleDone}
      />
    </div>
  );
}
